import { inject, Injectable } from '@angular/core'
import { ResourceService } from './resource.service'
import { Content } from '@monorepo/golab-generated'
import { AppConfigurationInformationService } from './app-configuration-information.service'
import { ResourceJSON } from '@monorepo/next-lab'
import { AppListService } from './app-list.service'
import { AppIdService } from './app-id.service'

// App configuration resources are highly dynamic per app id.
// Use a permissive alias here to keep serialization logic readable under strict index-signature settings.
// type ResourceJSON = any

interface BaseContentJson {
  id: string
  type: string
}

interface TextContentJson extends BaseContentJson {
  type: 'text'
  html: string
}

interface UrlContentJson extends BaseContentJson {
  type: 'image' | 'video' | 'webPage'
  url: string
}

interface FileContentJson extends BaseContentJson {
  type: 'file'
  fileName: string
}

interface AppContentJson extends BaseContentJson {
  type: 'app'
  // appId: string
  standardHelpHtml?: string
  content?: {
    customHelpHtml?: string
    [key: string]: any
  }
}

interface ReviewableAppContentJson extends AppContentJson {
  // Review serialization writes many app-specific dynamic keys.
  // Keep this permissive to avoid TS4111 (`noPropertyAccessFromIndexSignature`) noise.
  content: any
}

interface OtherContentJson extends BaseContentJson {
  type: 'lab' | 'customApp' | 'pdf' | 'notRecognized'
}

export type ContentJson = TextContentJson | UrlContentJson | AppContentJson | FileContentJson | OtherContentJson

@Injectable({ providedIn: 'root' })
export class ContentJsonCreatorService {
  private readonly appListService = inject(AppListService)
  private readonly resourceService = inject(ResourceService)
  private readonly appConfigurationInformationService = inject(AppConfigurationInformationService)
  private readonly appIdService = inject(AppIdService)

  async createContentJson(content: Content): Promise<ContentJson> {
    const baseContent = {
      id: content._id,
      type: content.contentType,
    }

    switch (content.contentType) {
      case 'text':
        return {
          ...baseContent,
          html: content.html,
        } as TextContentJson
      case 'image':
      case 'video':
      case 'webPage':
        return {
          ...baseContent,
          url: content.url,
        } as UrlContentJson
      case 'app':
        return this.createAppContentJson(content, baseContent)
      case 'file':
        return {
          ...baseContent,
          fileName: content.fileName,
        } as FileContentJson
      default:
        return {
          ...baseContent,
        } as OtherContentJson
    }
  }

  private async createAppContentJson(content: Content, baseContent: BaseContentJson): Promise<AppContentJson> {
    const appContentJson: AppContentJson = {
      ...baseContent,
      type: 'app',
      // appId: '',
    }
    const description = this.appListService.getDescriptionByUrl(content.url || '')
    if (!description) {
      return appContentJson
    }
    const appId = this.appIdService.getAppId(description.embedLink)
    appContentJson.id = appId ?? description._id
    const configurationResource = await this.resourceService.getAppConfiguration(content._id)
    if (!configurationResource) {
      return appContentJson
    }
    const forApplication = configurationResource.metadata.target.forApplication
    if (!forApplication) {
      return appContentJson
    }
    this.addAppHelpHtml(appContentJson, configurationResource)
    this.addAppContentJsonContent(appContentJson, configurationResource)
    return appContentJson
  }

  private addAppHelpHtml(appContentJson: AppContentJson, configurationResource: ResourceJSON): void {
    if (!this.appConfigurationInformationService.hasStudentHelp(configurationResource.metadata.target.forApplication)) {
      return
    }
    if (configurationResource.content.useConfigurationHelp) {
      appContentJson.content = appContentJson.content || {}
      appContentJson.content.customHelpHtml = configurationResource.content.help
    } else {
      appContentJson.standardHelpHtml = this.getStandardHelpHtml(configurationResource)
    }
  }

  private getStandardHelpHtml(configurationResource: ResourceJSON): string {
    if (configurationResource.content.useConfigurationHelp) {
      return configurationResource.content.help
    }
    // todo get help from language file
    return configurationResource.content.help
  }

  private addAppContentJsonContent(appContentJson: AppContentJson, configurationResource: ResourceJSON): void {
    if (!this.appConfigurationInformationService.isReviewable(configurationResource.metadata.target.forApplication)) {
      return
    }
    if (!configurationResource.content || Object.keys(configurationResource.content).length === 0) {
      return
    }
    appContentJson.content = appContentJson.content || {}
    const reviewableAppContentJson = appContentJson as ReviewableAppContentJson
    switch (configurationResource.metadata.target.forApplication) {
      case 'nameTheFrame':
        this.addNameTheFrameContent(reviewableAppContentJson, configurationResource)
        break
      case 'Quiz2':
        this.addQuizAppContent(reviewableAppContentJson, configurationResource, true)
        break
      case 'Questionnaire2':
        this.addQuizAppContent(reviewableAppContentJson, configurationResource, false)
        break
      case 'hypothesis':
        this.addHypothesisScratchpadContent(reviewableAppContentJson, configurationResource)
        break
      case 'questioning':
        this.addQuestionScratchpadContent(reviewableAppContentJson, configurationResource)
        break
      case 'cm2':
        this.addConceptMapperContent(reviewableAppContentJson, configurationResource)
        break
      case 'speechBubble':
        this.addSpeechBubbleContent(reviewableAppContentJson, configurationResource)
        break
      case 'viewer':
        this.addViewerContent(reviewableAppContentJson, configurationResource)
        break
      case 'fillInTheBlanks':
        this.addFillInTheBlanksContent(reviewableAppContentJson, configurationResource)
        break
      case 'calculator':
        this.addCalculatorContent(reviewableAppContentJson, configurationResource)
        break
      case 'activityPlot':
      case 'acm':
      case 'phaseTransitions':
      case 'timeChecker':
      case 'timeline':
        // case 'reflect3': // time spent in phases, has also definable student time norms
        this.addReflectionQuestionsContent(reviewableAppContentJson, configurationResource)
        break
      case 'reflect3': // time spent in phases, has also definable student time norms
        this.addTimeSpentContent(reviewableAppContentJson, configurationResource)
        break
      case 'Chat':
      case 'chatWithAI':
        this.addChatAppContent(reviewableAppContentJson, configurationResource)
        break
      case 'reportingTool':
        this.addReportToolContent(reviewableAppContentJson, configurationResource)
        break
      case 'tableTool':
        this.addTableToolContent(reviewableAppContentJson, configurationResource)
        break
      case 'timeplanner':
      case 'timePlanner2':
        this.addTimePlannerContent(reviewableAppContentJson, configurationResource)
        break
      case 'noteBoard':
        this.addNoteBoardContent(reviewableAppContentJson, configurationResource)
        break
      case 'videoPlayer':
        this.addVideoPlayerContent(reviewableAppContentJson, configurationResource)
        break
      case 'conceptSorter':
        this.addConceptSorterContent(reviewableAppContentJson, configurationResource)
        break
      case 'connectCorrect':
        this.addConnectDirectContent(reviewableAppContentJson, configurationResource)
        break
      case 'edt':
        this.addExperimentDesignToolContent(reviewableAppContentJson, configurationResource)
        break
      default:
        console.log(
          `no content adding function for app with id ${configurationResource.metadata.target.forApplication}`,
        )
        return undefined
    }
  }

  private addNameTheFrameContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.names = configurationResource.content.nameElements.map(
      (nameElement: any) => nameElement.name,
    )
  }

  private addQuizAppContent(
    appContentJson: ReviewableAppContentJson,
    configurationResource: ResourceJSON,
    hasCorrectAnswer: boolean,
  ): any {
    if (configurationResource.content.version === '0.0' || !configurationResource.content.version) {
      // it is the content from a refquest quiz
      return
    }
    const hasFeedback = configurationResource.content.feedbackType !== 'None'
    appContentJson.content.questions = configurationResource.content.questions.map((question: any) =>
      this.createQuestionJson(question, hasCorrectAnswer, hasFeedback),
    )
  }

  private addReflectionQuestionsContent(
    appContentJson: ReviewableAppContentJson,
    configurationResource: ResourceJSON,
  ): any {
    if (!configurationResource.content.reflectionQuestions?.showReflectionQuestions) {
      return
    }
    const hasFeedback = configurationResource.content.reflectionQuestions.feedbackType !== 'None'
    appContentJson.content.reflectionQuestions = configurationResource.content.reflectionQuestions.questions.map(
      (question: any) => this.createQuestionJson(question, false, hasFeedback),
    )
  }

  private createQuestionJson(question: any, hasCorrectAnswer: boolean, hasFeedback: boolean) {
    const answerDefinition = question.answerDefinition
    const questionJson: any = {
      type: answerDefinition.answerType,
      questionHtml: question.description,
      hint: question.hint,
    }
    switch (answerDefinition.answerType) {
      case 'None':
        break
      case 'Text':
        questionJson.placeHolder = answerDefinition.placeholder
        break
      case 'Radio':
      case 'Checkbox':
        questionJson.options = answerDefinition.answerOptions.map((option: any) =>
          this.createOptionAnswerJson(option, hasCorrectAnswer, hasFeedback),
        )
        break
      case 'YesNo': {
        questionJson.type = 'LeftOrRight'
        questionJson.leftOption = this.createOptionAnswerJson(answerDefinition.left, hasCorrectAnswer, hasFeedback)
        questionJson.rightOption = this.createOptionAnswerJson(answerDefinition.right, hasCorrectAnswer, hasFeedback)
        break
      }
      case 'Numeric': {
        this.addNumericInputTypeContent(questionJson, answerDefinition)
        if (hasCorrectAnswer) {
          questionJson.correct = answerDefinition.correctAnswer
        }
        break
      }
      case 'GridMultipleChoice': {
        questionJson.numberOfAllowedAnswerPerRow = answerDefinition.gridMultipleChoiceAnswerTypes
        questionJson.rows = answerDefinition.rowOptions.map((row: any) => row.text)
        questionJson.columns = answerDefinition.columnOptions.map((column: any) => column.text)
        break
      }
      case 'GridDropdown': {
        questionJson.rows = answerDefinition.rowOptions.map((row: any) => row.text)
        questionJson.columns = answerDefinition.columnOptions.map((column: any) => {
          return {
            text: column.text,
            dropdownOptions: column.dropdownOptions.map((option: any) => option.text),
          }
        })
        break
      }
      case 'GridSmileys': {
        if (answerDefinition.gridSmileysAnswerType === 'Smileys3') {
          questionJson.smileyOptions = ['angry', 'neutral', 'happy']
        } else {
          questionJson.smileyOptions = ['angry', 'disappointed', 'neutral', 'satisfied', 'happy']
        }
        questionJson.rows = answerDefinition.rowOptions.map((row: any) => row.text)
        break
      }
      case 'GridText': {
        questionJson.dynamicNumberOfRows = answerDefinition.dynamicNumberOfRows
        if (answerDefinition.dynamicNumberOfRows) {
          questionJson.minimumNumberOfRows = answerDefinition.minimumNumberOfRows
          questionJson.maximumNumberOfRows = answerDefinition.maximumNumberOfRows
        } else {
          questionJson.rows = answerDefinition.rowOptions.map((row: any) => row.text)
        }
        questionJson.columns = answerDefinition.columnOptions.map((column: any) => column.text)
        break
      }
      case 'GridNumeric': {
        this.addNumericInputTypeContent(questionJson, answerDefinition)
        questionJson.rows = answerDefinition.rowOptions.map((row: any) => row.text)
        questionJson.columns = answerDefinition.columnOptions.map((column: any) => column.text)
        break
      }
      default:
        console.log(`question type ${answerDefinition.answerType} not recognized`)
    }
    return questionJson
  }

  private createOptionAnswerJson(option: any, hasCorrectAnswer: boolean, hasFeedback: boolean): any {
    const answerOption: any = {
      option: option.text,
    }
    if (hasCorrectAnswer) {
      answerOption.correct = option.correct
      if (hasFeedback) {
        answerOption.feedbackText = option.feedback
      }
    }
    return answerOption
  }

  private addNumericInputTypeContent(questionJson: any, answerDefinition: any): any {
    questionJson.inputType = answerDefinition.useSlider ? 'slider' : 'textField'
    if (answerDefinition.useSlider) {
      questionJson.sliderMin = answerDefinition.sliderMin
      questionJson.sliderMax = answerDefinition.sliderMax
      questionJson.sliderStep = answerDefinition.sliderStep
    }
  }

  private addHypothesisScratchpadContent(
    appContentJson: ReviewableAppContentJson,
    configurationResource: ResourceJSON,
  ): any {
    appContentJson.content.showConfidenceEntry = configurationResource.content.confidence
    appContentJson.content.createOwnTerms = configurationResource.content.freeInput
    appContentJson.content.minimumNumberOfHypotheses = configurationResource.content.minimumNrOfItems
    appContentJson.content.maximumNumberOfHypotheses = configurationResource.content.maximumNrOfItems
    appContentJson.content.fixedPlacedIfThen = configurationResource.content.hypothesisMode === 'Limited'
    if (appContentJson.content.fixedPlacedIfThen) {
      appContentJson.content.conditionals = configurationResource.content.predefinedOperatorIds.slice()
    } else if (configurationResource.content.operators) {
      appContentJson.content.conditionals = configurationResource.content.operators.map(
        (operator: any) => operator.label,
      )
    }
    if (configurationResource.content.values) {
      appContentJson.content.variables = configurationResource.content.values.map((value: any) => value.label)
    }
  }

  private addQuestionScratchpadContent(
    appContentJson: ReviewableAppContentJson,
    configurationResource: ResourceJSON,
  ): any {
    appContentJson.content.minimumNumberOfQuestions = configurationResource.content.minimumNrOfItems
    appContentJson.content.maximumNumberOfQuestions = configurationResource.content.maximumNrOfItems
    appContentJson.content.conditionals = configurationResource.content.conditionals.slice()
    appContentJson.content.variables = configurationResource.content.variables.slice()
  }

  private addConceptMapperContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.concepts = configurationResource.content.concepts.slice()
    appContentJson.content.relations = configurationResource.content.relations.slice()
  }

  private addSpeechBubbleContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.html = configurationResource.content.speechBubbleLabel
  }

  private addViewerContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    // the review ils prompt describes what a missing and empty source app id means, so we can just set it to an empty string if it's not provided
    appContentJson.content.sourceAppId = configurationResource.content.sourceAppId
      ? configurationResource.content.sourceAppId
      : ''
    if (configurationResource.content.useCustomTitle) {
      appContentJson.content.customTitle = configurationResource.content.customTitle
    }
    if (configurationResource.content.useCustomNoResourceMessage) {
      appContentJson.content.customNoResourceMessage = configurationResource.content.customNoResourceMessage
    }
  }

  private addFillInTheBlanksContent(
    appContentJson: ReviewableAppContentJson,
    configurationResource: ResourceJSON,
  ): any {
    // the review ils prompt describes what a missing and empty source app id means, so we can just set it to an empty string if it's not provided
    appContentJson.content.html = configurationResource.content.fullText
    if (configurationResource.content.showAdditionalTerms) {
      appContentJson.content.additionalTerms = configurationResource.content.additionalTerms.map(
        (term: any) => term.text,
      )
    } else {
      appContentJson.content.additionalTerms = []
    }
    appContentJson.content.showValidateButton = configurationResource.content.showValidateButton
  }

  private addCalculatorContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.calculatorMode = configurationResource.content.calculatorMode
    appContentJson.content.nrOfDecimals = configurationResource.content.nrOfDecimals
    appContentJson.content.allowInfinityAndNaN = configurationResource.content.allowInfinityAndNaN
  }

  private addTimeSpentContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    this.addReflectionQuestionsContent(appContentJson, configurationResource)
    const phaseNormUnit = configurationResource.content.normType === 'Percentage' ? '%' : ' minutes'
    const phaseNorms = configurationResource.content.phaseNorms.map((phaseNorm: any) => ({
      id: phaseNorm.id,
      norm: phaseNorm.norm + phaseNormUnit,
    }))
    const totalTime = configurationResource.content.phaseNorms
      .map((phaseNorm: any) => phaseNorm.norm)
      .reduce((sum: number, norm: number) => sum + norm, 0)
    appContentJson.content.timeForPhaseNorms = phaseNorms
    appContentJson.content.totalTimeNorm = totalTime + phaseNormUnit
  }

  private addChatAppContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    if (configurationResource.content.showWelcomeMessage) {
      appContentJson.content.welcomeMessage = configurationResource.content.welcomeMessage
    }
  }

  private addReportToolContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.sections = configurationResource.content.reportSectionConfigurations.map((section: any) => ({
      title: section.title,
      explanationHtml: section.explanation,
    }))
  }

  private addTableToolContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.rowLabels = configurationResource.content.rowLabels.slice()
    appContentJson.content.columnLabels = configurationResource.content.columnLabels.slice()
    appContentJson.content.topLeftLabel = configurationResource.content.firstColumnLabel
  }

  private addTimePlannerContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    if (!configurationResource.content.phaseDescriptions) {
      // it is an old version
      return
    }
    appContentJson.content.totalTime = configurationResource.content.totalTime + ' minutes'
    appContentJson.content.phaseDescriptions = configurationResource.content.phaseDescriptions.map(
      (phaseDescription: any) => ({ id: phaseDescription.id, descriptionText: phaseDescription.description }),
    )
  }

  private addNoteBoardContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.allowEditingOfNotesCreatedByOthers = configurationResource.content.allowEditOthers
    appContentJson.content.boards = configurationResource.content.boardDefinitions.map(
      (boardDefinition: any) => boardDefinition.name,
    )
  }

  private addVideoPlayerContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    if (configurationResource.content.videoUrl) {
      appContentJson.content.videoUrl = configurationResource.content.videoUrl
      return
    }
    if (!configurationResource.content.videos || configurationResource.content.videos.length === 0) {
      return
    }
    if (configurationResource.content.videos.length === 1) {
      appContentJson.content.videoUrl = configurationResource.content.videos[0].url
      return
    }
    appContentJson.content.videos = configurationResource.content.videos.map((video: any) => ({
      name: video.name,
      url: video.url,
    }))
    if (configurationResource.content.customVideoSelectorLabel.useCustomText) {
      appContentJson.content.customVideoSelectorLabel =
        configurationResource.content.customVideoSelectorLabel.customText
    }
  }

  private addConceptSorterContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.showCheckAnswersButton = configurationResource.content.showCheckAnswersButton
    appContentJson.content.categories = configurationResource.content.categories.map((category: any) => category.name)
    appContentJson.content.concepts = configurationResource.content.concepts.map((concept: any) => concept.name)
  }

  private addConnectDirectContent(appContentJson: ReviewableAppContentJson, configurationResource: ResourceJSON): any {
    appContentJson.content.showCheckAnswersButton = configurationResource.content.showCheckAnswersButton
    const itemToJson = (item: any) => {
      const itemJson: any = { type: item.type }
      switch (item.type) {
        case 'text':
          itemJson.text = item.text
          break
        case 'image':
          itemJson.url = item.imgUrl
          break
        case 'audio':
          itemJson.url = item.audioUrl
          break
        default:
          console.log(`item type ${item.type} not recognized for connect direct app content`)
      }
      return itemJson
    }
    appContentJson.content.left = configurationResource.content.left.map(itemToJson)
    appContentJson.content.right = configurationResource.content.right.map(itemToJson)
  }

  private addExperimentDesignToolContent(
    appContentJson: ReviewableAppContentJson,
    configurationResource: ResourceJSON,
  ): any {
    appContentJson.content.limits = {
      numberOfExperimentalTrials: {
        minimum: configurationResource.content.configuration.required_experiments,
        maximum: configurationResource.content.configuration.max_experiments,
      },
      numberOfPropertiesToVary: configurationResource.content.configuration.independent,
      numberOfPropertiesToKeepConstant: configurationResource.content.configuration.control,
      numberOfMeasures: configurationResource.content.dependent,
    }
    if (configurationResource.content.configuration.variable.use_all) {
      appContentJson.content.limits.totalNumberOfProperties = { useAll: true }
    } else {
      appContentJson.content.limits.totalNumberOfProperties = {
        minimum: configurationResource.content.configuration.variable.minimum,
        maximum: configurationResource.content.configuration.variable.maximum,
      }
    }
    appContentJson.content.showInstructionOnHowToPreceed = configurationResource.content.configuration.show_instructions
    appContentJson.content.experimentTableIsSortable = configurationResource.content.configuration.experiments_sortable
    appContentJson.content.dataSetName =
      configurationResource.content.configuration.resource_name ?? 'Experiment Designs'
    const itemToJson = (item: any) => {
      const itemJson: any = { name: item.name }
      switch (item.input) {
        case 'text':
          break
        case 'numerical':
        case 'slider':
          itemJson.minimum = item.minimum
          itemJson.maximum = item.maximum
          itemJson.increment = item.increment
          itemJson.precision = item.precision
          itemJson.symbol = item.symbol
          itemJson.initial = item.initial
          break
        case 'categorical':
          itemJson.values = item.values.filter((value: string) => value.length > 0)
          break
        default:
          console.log(`item input type "${item.input}" not recognized for edt content`)
      }
      return itemJson
    }
    appContentJson.content.properties = configurationResource.content.specification.properties
      .filter((item: any) => item.name.length > 0)
      .map(itemToJson)
    appContentJson.content.measures = configurationResource.content.specification.measures
      .filter((item: any) => item.name.length > 0)
      .map(itemToJson)
  }
}
